import type { PlainAgentExecution } from "@/queries/agent-queries";

// Annotation key used to track which agent execution spawned a sub-agent.
// Matches ai-agents/runtime/tools/subagent.AnnotationKeyParentExecutionID.
export const ANNOTATION_PARENT_EXECUTION_ID = "ona.com/parent-execution-id";

// Subagent name prefix constant - matches ai-agents/runtime/tools/subagent
export const SUBAGENT_NAME_PREFIX = "[subagent:";

// Regex to parse subagent name: [subagent:<parent_id>] <task>
const SUBAGENT_NAME_REGEX = /^\[subagent:([a-fA-F0-9-]+)\]\s*(.*)$/;

/**
 * Check if an agent execution is a subagent based on its name.
 */
export function isSubagentExecution(execution: PlainAgentExecution): boolean {
    return execution.metadata?.name?.startsWith(SUBAGENT_NAME_PREFIX) ?? false;
}

/**
 * Parse subagent name to extract parent execution ID and task description.
 * Returns null if the name doesn't match the subagent format.
 */
export function parseSubagentName(name: string): { parentId: string; task: string } | null {
    const match = SUBAGENT_NAME_REGEX.exec(name);
    if (!match) return null;
    return { parentId: match[1], task: match[2] };
}

/**
 * Get the task name from a subagent execution.
 * Returns the task portion of the subagent name, or undefined if not a subagent.
 */
export function getSubagentTask(execution: PlainAgentExecution): string | undefined {
    const name = execution.metadata?.name;
    if (!name) return undefined;
    const parsed = parseSubagentName(name);
    return parsed?.task;
}

/**
 * Get the parent execution ID from the annotation on a sub-agent execution.
 * Returns undefined if the annotation is not present.
 */
export function getParentExecutionId(execution: PlainAgentExecution): string | undefined {
    return execution.metadata?.annotations?.[ANNOTATION_PARENT_EXECUTION_ID] || undefined;
}
